import React, { useEffect, useState } from 'react';
import { InputNumber, Toggle } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import { STANDARD_WORK_DAY_HOURS } from '../../utils/payPeriods';

const formatHours = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
  const asInt = Math.round(rounded);
  return Math.abs(rounded - asInt) < 0.00001 ? String(asInt) : String(rounded);
};

// The two things the reader controls about the projection: how long their day
// is, and whether they want to see it at all.
//
// Serves double duty. On first use nobody has answered, so the page opens this
// with `firstRun` set and the copy asks the question rather than offering to
// change an answer - there is nothing to change yet. Afterwards the same modal
// is what the gear beside "Projected" opens.
//
// The show/hide toggle is deliberately kept out of the first run. Being asked a
// question and being offered a way to dismiss the thing asking it, in the same
// breath, invites dismissing it without reading - and somebody who turns the
// projection off before ever seeing it has not made a judgement about it.
export default function ProjectionSettingsModal({
  open,
  onClose,
  onSave,
  saving = false,
  firstRun = false,
  averageWorkdayHours = null,
  hideProjection = false,
  nonExempt = false,
  // Worked out from the last four complete weeks of their own timesheet. Null
  // when there is no history to derive one from.
  suggestedWorkdayHours = null,
  recentWeeklyAverage = null,
  // Set when a manager is editing somebody else's timesheet. The working day is
  // a fact about that person and theirs to maintain, so the copy switches to
  // naming them - but the show/hide toggle below is a personal display
  // preference, so it is left off entirely rather than flipped on their behalf.
  subjectName = null,
}) {
  const editingSelf = !subjectName;
  const effectiveSuggestedWorkdayHours = nonExempt ? suggestedWorkdayHours : null;
  // Seeded from their answer, then the suggestion, then the standard day. The
  // suggestion comes second rather than first: an answer already given is a
  // deliberate one, and quietly replacing it with a computed number on the next
  // open would undo it without saying so.
  const [hours, setHours] = useState(
    () => averageWorkdayHours ?? effectiveSuggestedWorkdayHours ?? STANDARD_WORK_DAY_HOURS
  );
  // Held as "hidden" because that is what is stored: the column defaults to
  // false so the projection is on for everybody until they say otherwise, and a
  // column that defaults to true reads backwards in the schema. The toggle
  // below shows the reader the positive of it.
  const [hidden, setHidden] = useState(hideProjection);
  const [error, setError] = useState(null);

  // Reseed from the saved values each time it opens, so a cancelled edit is
  // actually cancelled rather than kept around for the next open.
  useEffect(() => {
    if (!open) return;
    setHours(averageWorkdayHours ?? effectiveSuggestedWorkdayHours ?? STANDARD_WORK_DAY_HOURS);
    setHidden(hideProjection);
    setError(null);
  }, [open, averageWorkdayHours, effectiveSuggestedWorkdayHours, hideProjection]);

  const handleSave = () => {
    const value = Number(hours);
    if (!Number.isFinite(value) || value <= 0 || value > 24) {
      setError('Enter a number of hours greater than 0 and up to 24.');
      return;
    }
    // hideProjection is omitted when the toggle was not on screen, so the
    // caller writes the hours alone rather than restating a preference the
    // editor was never shown.
    onSave(editingSelf
      ? { averageWorkdayHours: value, hideProjection: hidden }
      : { averageWorkdayHours: value });
  };

  // Offered rather than applied once they have an answer of their own, and only
  // when taking it would actually change the number in the field.
  const canApplySuggestion = effectiveSuggestedWorkdayHours != null
    && Math.abs(Number(hours) - effectiveSuggestedWorkdayHours) > 0.001;

  return (
    <Modal open={open} onClose={onClose} size="xs">
      <Modal.Header>
        <Modal.Title>
          {!firstRun
            ? 'Projection settings'
            : editingSelf ? 'How long is your working day?' : `How long is ${subjectName}'s working day?`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small">
          {firstRun && editingSelf ? (
            <>
              The projection works out where this pay period lands by crediting
              every working day you have left. To do that it needs to know what
              a day looks like for you - not everybody works eight hours.
            </>
          ) : firstRun ? (
            <>
              The projection works out where this pay period lands by crediting
              every working day {subjectName} has left. To do that it needs to
              know what a day looks like for them - not everybody works eight
              hours.
            </>
          ) : editingSelf ? (
            <>
              The projection credits every working day you have left at this
              many hours. It only affects the forecast: your{' '}
              {nonExempt ? 'target hours for the period are set on your profile' : 'hours to work for the period come from your commitment'}
              {' '}and do not change.
            </>
          ) : (
            <>
              The projection credits every working day {subjectName} has left at
              this many hours. It only affects the forecast: their{' '}
              {nonExempt ? 'target hours for the period are set on their profile' : 'hours to work for the period come from their commitment'}
              {' '}and do not change.
            </>
          )}
        </p>

        <div className="mt-3">
          <label className="form-label small fw-semibold" htmlFor="average-workday-hours">
            Hours on an average working day
          </label>
          <InputNumber
            id="average-workday-hours"
            value={hours}
            onChange={(value) => { setHours(value); setError(null); }}
            min={0.5}
            max={24}
            step={0.5}
            postfix="hrs"
            style={{ width: '100%' }}
          />
          {error ? <div className="text-danger small mt-1">{error}</div> : null}

          {/* Their own timesheet already knows roughly what a day looks like,
              so the question arrives with its own answer rather than sending
              them off to work one out. Still only a suggestion: a stretch of
              unusual weeks should not become their standing figure unless they
              agree to it. */}
          {effectiveSuggestedWorkdayHours != null ? (
            <div className="text-muted small mt-2">
              {editingSelf ? 'You' : subjectName} averaged{' '}
              <span className="fw-semibold">
                {formatHours(recentWeeklyAverage?.hoursPerWeek)} hrs a week
              </span>{' '}
              over the last {recentWeeklyAverage?.weeks ?? 4} complete weeks,
              which is about{' '}
              <span className="fw-semibold">{formatHours(effectiveSuggestedWorkdayHours)} hrs</span>{' '}
              a day across a five-day week.
              {canApplySuggestion ? (
                <Button
                  size="sm"
                  variant="accent"
                  className="ms-1"
                  onClick={() => { setHours(effectiveSuggestedWorkdayHours); setError(null); }}
                >
                  Use {formatHours(effectiveSuggestedWorkdayHours)} hrs
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {firstRun || !editingSelf ? null : (
          <div className="d-flex align-items-center justify-content-between mt-4">
            <div>
              <div className="small fw-semibold">Show the projection</div>
              <div className="text-muted small">
                Off leaves only your hours worked on the card.
              </div>
            </div>
            {/* Phrased as the thing being switched on, so the toggle reads with
                the grain: on means you get the projection, which is where
                everybody starts. The stored column is the negative of this -
                see the note on `hidden` above. */}
            <Toggle checked={!hidden} onChange={(checked) => setHidden(!checked)} />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="primary" loading={saving}>
          {!firstRun ? 'Save' : editingSelf ? 'Show my projection' : 'Show projection'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
